import { createSceneNodeEntityId } from '../ids/entityIds.js';
import { vec2, type Vec2 } from './layoutMath.js';
import type { DataGraphInstance, SceneDataContext } from '../graph/dataGraph.js';
import type { SceneEventBindings } from '../graph/eventGraph.js';

// Scene model: tree of SceneNodes with FrameNode containers and InstanceNode leaves.

export type SceneNodeId = string;
export type ComponentId = string;
export type LayerId = string;

export type ObjectFit = 'contain' | 'cover' | 'fill';

/** CSS align-self. 'auto' inherits the parent frame's align-items. */
export type AlignSelf = 'auto' | 'stretch' | 'flex-start' | 'center' | 'flex-end';
/** CSS align-items (default cross-axis alignment for flex children). */
export type AlignItems = 'stretch' | 'flex-start' | 'center' | 'flex-end';
/** CSS justify-content (main-axis free-space distribution). */
export type JustifyContent = 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';

export type FramePadding = [top: number, right: number, bottom: number, left: number];

export type FrameLayout =
  | { kind: 'free' }
  | {
      kind: 'row' | 'column';
      gap: number;
      padding: FramePadding;
      alignItems: AlignItems;
      justifyContent: JustifyContent;
    }
  | {
      kind: 'stack';
      padding: FramePadding;
      alignItems: AlignItems;
      justifyContent: JustifyContent;
    };

export type FrameLayoutKind = FrameLayout['kind'];

export function createFrameLayout(kind: FrameLayoutKind = 'free'): FrameLayout {
  if (kind === 'free') return { kind: 'free' };
  const base = {
    padding: [0, 0, 0, 0] as FramePadding,
    alignItems: 'stretch' as AlignItems,
    justifyContent: 'flex-start' as JustifyContent,
  };
  return kind === 'stack'
    ? { kind, ...base }
    : { kind, gap: 0, ...base };
}

/** Layer/layout dimension. Percent resolves against the parent layout content size. */
export type DimValue =
  | { kind: 'auto' }
  | { kind: 'px'; value: number }
  | { kind: 'percent'; value: number };

export function dimAuto(): DimValue {
  return { kind: 'auto' };
}

export function dimPx(value: number): DimValue {
  return { kind: 'px', value };
}

export function dimPercent(value: number): DimValue {
  return { kind: 'percent', value };
}

export function normalizeDimValue(value: DimValue | number | null | undefined): DimValue {
  if (typeof value === 'number') return dimPx(Number.isFinite(value) ? value : 0);
  if (value && typeof value === 'object') {
    if (value.kind === 'px') return dimPx(Number.isFinite(value.value) ? value.value : 0);
    if (value.kind === 'percent') return dimPercent(Number.isFinite(value.value) ? value.value : 0);
  }
  return dimAuto();
}

export function resolveDimValue(value: DimValue | number | null | undefined, parentSize: number): number | null {
  const dim = normalizeDimValue(value);
  if (dim.kind === 'auto') return null;
  if (dim.kind === 'percent') return parentSize * dim.value * 0.01;
  return dim.value;
}

export interface SceneTransformProps {
  position: Vec2;
  rotation: number;
  scale: Vec2;
  /** Transform origin, normalized 0-1 (0 = left/top, 0.5 = center, 1 = right/bottom). */
  origin: Vec2;
  visible: boolean;
}

export interface FrameNode extends SceneTransformProps {
  kind: 'frame';
  id: SceneNodeId;
  name: string;
  childIds: SceneNodeId[];
  clip: boolean;
  /** How this frame arranges its children. */
  layout: FrameLayout;
  /** Width dimension. auto = size from layout / children. */
  width?: DimValue;
  /** Height dimension. auto = size from layout / children. */
  height?: DimValue;
  /** CSS flex-grow when this frame is a flex item inside another container. */
  flexGrow: number;
  /** CSS flex-shrink when this frame is a flex item inside another container. */
  flexShrink: number;
  /** CSS flex-basis: initial main-axis size before grow/shrink. auto = use measured size. */
  flexBasis: DimValue;
  /** CSS align-self when nested inside a flex container. */
  alignSelf: AlignSelf;
  behavior?: DataGraphInstance;
  events?: SceneEventBindings;
  dataContext?: SceneDataContext;
}

export interface InstanceNode extends SceneTransformProps {
  kind: 'instance';
  id: SceneNodeId;
  name: string;
  componentId: ComponentId;
  paramOverrides: Record<string, unknown>;
  fit: ObjectFit;
  /** Width dimension. auto lets flex determine it. */
  width: DimValue;
  /** Height dimension. auto lets flex determine it. */
  height: DimValue;
  /** CSS flex-grow: share of available main-axis free space. */
  flexGrow: number;
  /** CSS flex-shrink: share of overflow to absorb. 0 = never shrink. */
  flexShrink: number;
  /** CSS flex-basis: initial main-axis size before grow/shrink. auto = use width/height. */
  flexBasis: DimValue;
  /** CSS align-self: 'auto' inherits the parent frame's align-items. */
  alignSelf: AlignSelf;
  behavior?: DataGraphInstance;
  events?: SceneEventBindings;
  dataContext?: SceneDataContext;
}

export interface LayerNode extends SceneTransformProps {
  kind: 'layer';
  id: SceneNodeId;
  name: string;
  layerId: LayerId;
  paramOverrides: Record<string, unknown>;
  fit: ObjectFit;
  /** Width dimension. auto lets flex determine it. */
  width: DimValue;
  /** Height dimension. auto lets flex determine it. */
  height: DimValue;
  /** CSS flex-grow. */
  flexGrow: number;
  /** CSS flex-shrink. */
  flexShrink: number;
  /** CSS flex-basis. */
  flexBasis: DimValue;
  /** CSS align-self. */
  alignSelf: AlignSelf;
  behavior?: DataGraphInstance;
  events?: SceneEventBindings;
  dataContext?: SceneDataContext;
}

export type SceneNode = FrameNode | InstanceNode | LayerNode;

export interface Scene {
  rootIds: SceneNodeId[];
  nodes: Record<SceneNodeId, SceneNode>;
}

export interface ComponentDef {
  id: ComponentId;
  name: string;
  scene: Scene;
}

// ── Factories ──────────────────────────────────────────────────────

export function createSceneNodeId(): SceneNodeId {
  return createSceneNodeEntityId();
}

export function createFrameNode(overrides?: Partial<Omit<FrameNode, 'kind'>>): FrameNode {
  return {
    kind: 'frame',
    id: createSceneNodeId(),
    name: 'Frame',
    childIds: [],
    position: vec2(0, 0),
    rotation: 0,
    scale: vec2(1, 1),
    origin: vec2(0.5, 0.5),
    visible: true,
    clip: false,
    layout: createFrameLayout('free'),
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: dimAuto(),
    alignSelf: 'auto',
    ...overrides,
  };
}

export function createInstanceNode(componentId: ComponentId, overrides?: Partial<Omit<InstanceNode, 'kind'>>): InstanceNode {
  return {
    kind: 'instance',
    id: createSceneNodeId(),
    name: 'Instance',
    componentId,
    paramOverrides: {},
    fit: 'contain',
    width: dimAuto(),
    height: dimAuto(),
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: dimAuto(),
    alignSelf: 'auto',
    position: vec2(0, 0),
    rotation: 0,
    scale: vec2(1, 1),
    origin: vec2(0.5, 0.5),
    visible: true,
    ...overrides,
  };
}

export function createLayerNode(layerId: LayerId, overrides?: Partial<Omit<LayerNode, 'kind'>>): LayerNode {
  return {
    kind: 'layer',
    id: createSceneNodeId(),
    name: 'Layer',
    layerId,
    paramOverrides: {},
    fit: 'contain',
    width: dimAuto(),
    height: dimAuto(),
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: dimAuto(),
    alignSelf: 'auto',
    position: vec2(0, 0),
    rotation: 0,
    scale: vec2(1, 1),
    origin: vec2(0.5, 0.5),
    visible: true,
    ...overrides,
  };
}

// ── Default scene ──────────────────────────────────────────────────

const DEFAULT_COMPONENT_ID: ComponentId = 'component_main';
const DEFAULT_LAYER_ID: LayerId = 'layer_main';

/** Create a minimal default scene: one root FrameNode containing one InstanceNode. */
export function createDefaultScene(componentId: ComponentId = DEFAULT_COMPONENT_ID): Scene {
  const root = createFrameNode({ name: 'Root' });
  const instance = createInstanceNode(componentId);
  root.childIds.push(instance.id);

  return {
    rootIds: [root.id],
    nodes: {
      [root.id]: root,
      [instance.id]: instance,
    },
  };
}

/** Create a default standalone viewport scene: root FrameNode containing one LayerNode. */
export function createDefaultLayerScene(layerId: LayerId = DEFAULT_LAYER_ID): Scene {
  const root = createFrameNode({ name: 'Root' });
  const layer = createLayerNode(layerId);
  root.childIds.push(layer.id);

  return {
    rootIds: [root.id],
    nodes: {
      [root.id]: root,
      [layer.id]: layer,
    },
  };
}
